import { DateTime } from '@components/misc/dateTime/dateTime';
import { FlagPriority } from '@generated/graphql';
import Link from '@mui/material/Link';
import { Badge } from '@ovComponents/2-component/badge/badge';
import type { ColumnDef } from '@ovComponents/2-component/dataTable';
import {
  createDataTableSelectColumn,
  type DataTableColumnMeta,
} from '@ovComponents/2-component/dataTable';
import { Link as RouterLink } from '@ovComponents/2-component/orgLink/orgLink';
import { generateClientNameString } from '@utils/generateClientNameString';
import type { Theme } from 'hephaestus/core';
import { Typography } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import type { MouseEvent } from 'react';
import {
  agingDays,
  badgeSeverityFromPriority,
  FLAG_CATEGORY_TO_I18N,
  getReconFlagPriorityBadgeSx,
  getReconFlagPriorityFilledSurface,
  getReconFlagStateBadgeSx,
  getReconFlagStateLabel,
} from '../utils/reconFlags.config';
import type {
  ReconFlagRow,
  ReconFlagSeverity,
} from '../utils/reconFlags.types';
import { AgingColumnHeader } from './agingColumnHeader';

const FLAG_CELL_BADGE_SX = {
  px: 2.5,
  py: 1,
  lineHeight: 1,
  minHeight: 'unset',
  borderRadius: 3,
  padding: '3px 10px',
  fontSize: 11,
  fontWeight: 600,
} as const;

type BuildReconFlagColumnsOptions = {
  /** When false, omits the checkbox column (bulk dismiss is FLAGGED-only). */
  enableBulkSelection?: boolean;
};

export const buildReconFlagColumns = (
  t: TFunction,
  theme: Theme,
  { enableBulkSelection = true }: BuildReconFlagColumnsOptions = {},
): ColumnDef<ReconFlagRow, unknown>[] => {
  const headDefaults: DataTableColumnMeta = {
    headerLabelVariant: 'bodySmall',
    headerLabelWeight: 'bold',
    headerLabelSx: {
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    headerTextColor: theme.color.onSurfaceVariant,
  };

  const dt = (
    partial: Partial<DataTableColumnMeta> = {},
  ): { dataTable: DataTableColumnMeta } => ({
    dataTable: {
      ...headDefaults,
      disableDataTableSortHandling: true,
      ...partial,
    },
  });

  const selectionColumn = createDataTableSelectColumn<ReconFlagRow>({
    testIdPrefix: 'recon-flags',
    enableSelectAll: true,
    checkboxSize: 'medium',
  });

  const dataColumns: ColumnDef<ReconFlagRow, unknown>[] = [
    {
      id: 'client',
      header: () => t('table.client'),
      cell: ({ row }) => {
        const flag = row.original;
        const user = flag.account?.user;
        const userId = user?.id;
        if (
          !userId ||
          (!user?.entityName && !(user?.firstName && user?.lastName))
        ) {
          return null;
        }
        return (
          <Link
            component={RouterLink}
            to={`/clients/${userId}`}
            target='_blank'
            rel='noopener noreferrer'
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
            }}
            underline='none'
            sx={{
              color: theme.color.onSurface,
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {generateClientNameString(user, false, true)}
          </Link>
        );
      },
      meta: dt({}),
    },
    {
      id: 'type',
      accessorKey: 'type',
      header: () => t('reconFlags.filters.flagType'),
      cell: ({ row }) => {
        const { bgcolor, color } = getReconFlagPriorityFilledSurface(
          theme,
          row.original.priority,
        );
        return (
          <Badge
            label={row.original.type}
            color='neutral'
            variant='text'
            sx={{
              ...FLAG_CELL_BADGE_SX,
              backgroundColor: bgcolor,
              color,
              borderColor: bgcolor,
            }}
          />
        );
      },
      meta: dt({}),
    },
    {
      id: 'category',
      header: () => t('reconFlags.filters.category'),
      cell: ({ row }) => {
        const cat = row.original.category;
        if (cat == null) {
          return (
            <Typography variant='bodySmall' sx={{ color: 'text.secondary' }}>
              —
            </Typography>
          );
        }
        if (!(cat in FLAG_CATEGORY_TO_I18N)) {
          return <Typography variant='bodySmall'>{cat}</Typography>;
        }
        const categoryKey = FLAG_CATEGORY_TO_I18N[cat];
        return (
          <Typography variant='bodySmall'>
            {t(`reconFlags.categories.${categoryKey}`)}
          </Typography>
        );
      },
      meta: dt({}),
    },
    {
      id: 'account',
      header: () => t('reconFlags.table.account'),
      cell: ({ row }) => {
        const flag = row.original;
        const userId = flag.account?.user?.id;
        const accountId = flag.account?.id;
        const custodianRaw =
          flag.account?.custodianAccountNumber ||
          (flag.details as { custodianAccountNumber?: string } | undefined)
            ?.custodianAccountNumber ||
          '';
        if (accountId && userId) {
          return (
            <Link
              component={RouterLink}
              to={`/clients/${userId}/account/${accountId}`}
              target='_blank'
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
              }}
            >
              {custodianRaw}
            </Link>
          );
        }
        return custodianRaw;
      },
      meta: dt({}),
    },
    {
      id: 'priority',
      header: () => t('reconFlags.filters.priority'),
      cell: ({ row }) => {
        const priority = row.original.priority;
        if (priority == null) {
          return (
            <Typography variant='bodySmall' sx={{ color: 'text.secondary' }}>
              —
            </Typography>
          );
        }
        const labelKey =
          priority === FlagPriority.Unknown
            ? 'UNKNOWN'
            : (priority as ReconFlagSeverity);
        const severity = badgeSeverityFromPriority(priority);
        return (
          <Badge
            label={t(`reconFlags.severityLabel.${labelKey}`)}
            color='neutral'
            variant='text'
            sx={{
              ...FLAG_CELL_BADGE_SX,
              ...getReconFlagPriorityBadgeSx(theme, severity),
            }}
          />
        );
      },
      meta: dt({}),
    },
    {
      id: 'state',
      accessorKey: 'state',
      header: () => t('state'),
      cell: ({ row }) => {
        const state = row.original.state;
        const label = getReconFlagStateLabel(t, state);
        return (
          <Badge
            label={label}
            color='neutral'
            variant='text'
            sx={{
              ...FLAG_CELL_BADGE_SX,
              ...getReconFlagStateBadgeSx(theme, state),
            }}
          />
        );
      },
      meta: dt({}),
    },
    {
      id: 'aging',
      header: () => <AgingColumnHeader t={t} theme={theme} />,
      cell: ({ row }) => {
        const days = agingDays(row.original.firstSeenAt);
        return (
          <Typography variant='bodySmall'>
            {t('reconFlags.agingDays', { count: days })}
          </Typography>
        );
      },
      meta: dt({ headerBare: true }),
    },
    {
      id: 'firstSeenAt',
      accessorKey: 'firstSeenAt',
      header: () => t('reconFlags.table.firstSeen'),
      cell: ({ row }) => (
        <DateTime variant='bodySmall' date={row.original.firstSeenAt} />
      ),
      meta: dt({}),
    },
  ];

  return enableBulkSelection ? [selectionColumn, ...dataColumns] : dataColumns;
};
